import type { PresetModel } from "@defibrillator/preset-core";
import { renderApproximate } from "@defibrillator/preview-engine";
import type { VitalTargetVersion } from "@defibrillator/vital-adapter";
import { useEffect, useRef, useState } from "react";
import { useConfirmation } from "@defibrillator/ui";
import type { LocalPresetRecord, PresetSaveResponse } from "../../shared/types.js";
import {
  isDraftDirty,
  type PresetEditorSnapshot,
} from "../lib/editor-state.js";
import { downloadVital } from "../lib/download-vital.js";
import { formatUserError } from "../lib/generation-status-labels.js";
import { encodeWavMonoBlob } from "../lib/encode-wav.js";
import { buildEditorDiscardConfirmationCopy } from "../lib/library-delete-confirmation.js";
import { RefinePresetError, refinePreset } from "../lib/refine-preset.js";
import { PresetSaveError, savePresetDraft } from "../lib/save-preset.js";
import { PresetControls } from "./PresetControls.js";
import { PreviewPlayer } from "./PreviewPlayer.js";
import { PromptInput } from "./PromptInput.js";

export type PresetEditorProps = {
  snapshot: PresetEditorSnapshot;
  online: boolean;
  volume: number;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onSaved: (saved: PresetSaveResponse, previewBlob?: Blob) => Promise<void> | void;
  onRefined: (result: {
    model: PresetModel;
    ops: LocalPresetRecord["ops"];
    versionId: string;
    contentHash?: string;
    vitalVersion?: VitalTargetVersion;
    jobId?: string;
    generationId: string;
    slug?: string;
  }) => Promise<void> | void;
  onConflict: (currentVersionId: string | null) => void;
};

export function PresetEditor({
  snapshot,
  online,
  volume,
  onClose,
  onDirtyChange,
  onSaved,
  onRefined,
  onConflict,
}: PresetEditorProps) {
  const confirm = useConfirmation();
  const closeConfirmBusyRef = useRef(false);
  const [draft, setDraft] = useState<PresetModel>(() => structuredClone(snapshot.savedModel));
  const [savedModel, setSavedModel] = useState<PresetModel>(() =>
    structuredClone(snapshot.savedModel),
  );
  const [baseVersionId, setBaseVersionId] = useState(snapshot.baseVersionId);
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState<"save" | "refine" | "preview" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draftPreviewUrl, setDraftPreviewUrl] = useState<string | null>(null);
  const [draftPreviewBlob, setDraftPreviewBlob] = useState<Blob | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const previewGenRef = useRef(0);
  const refineKeyRef = useRef<string | null>(null);
  const draftPreviewBlobRef = useRef<Blob | null>(null);
  const editorTopRef = useRef<HTMLElement | null>(null);

  const dirty = isDraftDirty(draft, savedModel);
  const serverBusy = busy === "save" || busy === "refine";

  function scrollToEditorTop() {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    editorTopRef.current?.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }

  useEffect(() => {
    setDraft(structuredClone(snapshot.savedModel));
    setSavedModel(structuredClone(snapshot.savedModel));
    setBaseVersionId(snapshot.baseVersionId);
    setInstruction("");
    setError(null);
    draftPreviewBlobRef.current = null;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setDraftPreviewUrl(null);
    setDraftPreviewBlob(null);
    // Reset only when opening a different versioned preset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.presetId, snapshot.baseVersionId]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  function setManagedDraftPreview(url: string | null, blob: Blob | null) {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    if (url?.startsWith("blob:")) {
      objectUrlRef.current = url;
    }
    setDraftPreviewUrl(url);
    setDraftPreviewBlob(blob);
    draftPreviewBlobRef.current = blob;
  }

  async function requestClose() {
    if (closeConfirmBusyRef.current) return;
    if (dirty) {
      closeConfirmBusyRef.current = true;
      try {
        const copy = buildEditorDiscardConfirmationCopy();
        const confirmed = await confirm({
          title: copy.title,
          description: copy.description,
          confirmLabel: copy.confirmLabel,
          tone: copy.tone,
        });
        if (!confirmed) return;
      } finally {
        closeConfirmBusyRef.current = false;
      }
    }
    onClose();
  }

  function onRevert() {
    setDraft(structuredClone(savedModel));
    setManagedDraftPreview(null, null);
    setError(null);
  }

  async function persistDraft(currentDraft: PresetModel, versionId: string) {
    const saved = await savePresetDraft({
      presetId: snapshot.presetId,
      baseVersionId: versionId,
      model: currentDraft,
    });
    setSavedModel(structuredClone(saved.model));
    setDraft(structuredClone(saved.model));
    setBaseVersionId(saved.versionId);
    await onSaved(saved, draftPreviewBlobRef.current ?? undefined);
    return saved;
  }

  async function onPreview() {
    const gen = ++previewGenRef.current;
    setBusy("preview");
    setError(null);
    try {
      const approx = renderApproximate(draft);
      const blob = encodeWavMonoBlob(approx.samples, approx.sampleRate);
      if (gen !== previewGenRef.current) return;
      setManagedDraftPreview(URL.createObjectURL(blob), blob);
    } catch (err) {
      if (gen !== previewGenRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (gen === previewGenRef.current) setBusy(null);
    }
  }

  async function onSave() {
    if (!dirty || !online) return;
    setBusy("save");
    setError(null);
    try {
      await persistDraft(draft, baseVersionId);
    } catch (err) {
      if (err instanceof PresetSaveError && err.status === 409) {
        const body = err.body as { currentVersionId?: string | null };
        onConflict(body.currentVersionId ?? null);
        setError("Version conflict — another tab saved first. Reload current or discard.");
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(null);
    }
  }

  async function onRefine() {
    if (!online || !instruction.trim()) return;
    scrollToEditorTop();
    setBusy("refine");
    setError(null);
    try {
      let versionId = baseVersionId;
      if (dirty) {
        const saved = await persistDraft(draft, baseVersionId);
        versionId = saved.versionId;
      }

      if (!refineKeyRef.current) {
        refineKeyRef.current = crypto.randomUUID();
      }
      const result = await refinePreset({
        presetId: snapshot.presetId,
        baseVersionId: versionId,
        instruction: instruction.trim(),
        idempotencyKey: refineKeyRef.current,
      });
      if (!result.versionId) {
        throw new RefinePresetError("refine_missing_version");
      }
      setDraft(structuredClone(result.model));
      setSavedModel(structuredClone(result.model));
      setBaseVersionId(result.versionId);
      setManagedDraftPreview(null, null);
      setInstruction("");
      refineKeyRef.current = null;
      await onRefined({
        model: result.model,
        ops: result.ops,
        versionId: result.versionId,
        contentHash: result.contentHash,
        vitalVersion: result.vitalVersion,
        jobId: result.jobId,
        generationId: result.generationId,
        slug: result.slug ?? snapshot.slug,
      });
    } catch (err) {
      if (err instanceof RefinePresetError && err.code === "version_conflict") {
        onConflict(err.currentVersionId ?? null);
        setError("Version conflict during refinement. Reload current or discard.");
      } else if (err instanceof PresetSaveError && err.status === 409) {
        const body = err.body as { currentVersionId?: string | null };
        onConflict(body.currentVersionId ?? null);
        setError("Version conflict — save before refine failed.");
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      ref={editorTopRef}
      className="panel preset-editor"
      aria-label="Preset editor"
    >
      <div className="editor-sticky">
        <div className="editor-header">
          <div>
            <h2>Edit preset</h2>
            <p className="editor-sub">
              {draft.name} · version {baseVersionId.slice(0, 8)}
              {dirty ? " · unsaved" : " · saved"}
              {busy === "refine" ? " · refining…" : ""}
            </p>
          </div>
          <button type="button" className="btn ghost btn-compact" onClick={() => void requestClose()}>
            Close
          </button>
        </div>

        <div className="editor-actions">
          <button
            type="button"
            className="btn"
            disabled={!dirty || !online || serverBusy}
            onClick={() => void onSave()}
          >
            {busy === "save" ? "Saving…" : "Save changes"}
          </button>
          <button
            type="button"
            className="btn ghost"
            disabled={!dirty || serverBusy}
            onClick={onRevert}
          >
            Revert
          </button>
          <button
            type="button"
            className="btn ghost"
            disabled={serverBusy}
            onClick={() => downloadVital(draft, snapshot.vitalVersion)}
          >
            Download draft
          </button>
        </div>

        <div className="editor-preview-bar">
          {draftPreviewUrl ? (
            <PreviewPlayer url={draftPreviewUrl} volume={volume} />
          ) : (
            <>
              <button
                type="button"
                className="btn"
                disabled={busy === "preview" || serverBusy}
                onClick={() => void onPreview()}
              >
                {busy === "preview" ? "Rendering…" : "Play preview"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="editor-refine">
        <h3>Refine with AI</h3>
        <p className="editor-hint">
          The saved preset stays authoritative. Refinement also receives its recent prompt
          history{dirty ? " after saving your manual edits" : ""}.
        </p>
        {snapshot.prompt ? (
          <p className="editor-prompt-echo">Reference prompt: {snapshot.prompt}</p>
        ) : null}
        <PromptInput
          id={`refine-prompt-${snapshot.presetId}`}
          label="Refinement prompt"
          value={instruction}
          disabled={serverBusy || !online}
          placeholder="make it wider and soften the attack"
          onChange={(value) => {
            setInstruction(value);
            refineKeyRef.current = null;
          }}
        />
        <button
          type="button"
          className="btn"
          disabled={!online || serverBusy || !instruction.trim()}
          onClick={() => void onRefine()}
        >
          {busy === "refine"
            ? "Refining…"
            : dirty
              ? "Save & refine"
              : "Refine with AI"}
        </button>
      </div>

      <details className="editor-controls-collapse">
        <summary className="editor-controls-collapse-summary">
          <span className="editor-controls-chevron" aria-hidden="true" />
          <span className="editor-controls-collapse-label">Manual preset controls</span>
          {dirty ? <span className="editor-controls-badge">unsaved edits</span> : null}
        </summary>
        <div className="editor-controls-collapse-body">
          <PresetControls
            model={draft}
            disabled={serverBusy}
            onChange={(next) => {
              setDraft(next);
              if (draftPreviewUrl) setManagedDraftPreview(null, null);
            }}
          />
        </div>
      </details>

      {error ? <p className="status-error">{formatUserError(error)}</p> : null}
    </section>
  );
}
